import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Target, Plus, Edit3, Trash2, MessageCircle, ChevronDown, Pencil, X, Check } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Goal } from '@/hooks/useGoals';
import { getGoalProgressPercentage } from '@/utils/goalProgressUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { usePremium } from '@/hooks/usePremium';
import { usePremiumGate } from '@/contexts/PremiumGateContext';
import { FREE_MAX_ACTIVE_GOALS } from '@/lib/premiumFeatures';
import { useUnits } from '@/hooks/useUnits';

interface GoalProgressCardProps {
  goals: Goal[];
  onCreateGoal: (goal: Omit<Goal, 'id' | 'created_at' | 'updated_at'>) => void;
  onUpdateProgress: (goalId: string, newValue: number) => void;
  onDeleteGoal: (goalId: string) => void;
}

export const GoalProgressCard = ({ goals, onCreateGoal, onUpdateProgress, onDeleteGoal }: GoalProgressCardProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPremium } = usePremium();
  const { openPaywall } = usePremiumGate();
  const units = useUnits();
  const activeGoalCount = goals.filter((g) => g.is_active).length;
  const canAddGoal = isPremium || activeGoalCount < FREE_MAX_ACTIVE_GOALS;
  const defaultWeightUnit = units === 'imperial' ? 'lbs' : 'kg';
  const defaultDistanceUnit = units === 'imperial' ? 'miles' : 'km';
  const { profile, updateProfile } = useProfile();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showOverallGoalsEditor, setShowOverallGoalsEditor] = useState(false);
  const [editingOverallGoals, setEditingOverallGoals] = useState<string[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [newProgress, setNewProgress] = useState('');
  const [newGoal, setNewGoal] = useState({
    goal_type: 'strength',
    exercise_name: '',
    target_value: 0,
    target_unit: 'kg',
    current_value: 0,
    starting_value: null as number | null,
    target_date: null as string | null,
    is_active: true,
    description: ''
  });

  useEffect(() => {
    setNewGoal((prev) => ({
      ...prev,
      target_unit:
        prev.goal_type === 'running'
          ? defaultDistanceUnit
          : prev.goal_type === 'endurance'
            ? 'reps'
            : defaultWeightUnit,
    }));
  }, [defaultWeightUnit, defaultDistanceUnit]);

  const primaryGoalOptions = [
    'Build Muscle',
    'Lose Weight',
    'Improve Strength',
    'Increase Endurance',
    'Better Health',
    'Sport Performance',
    'Flexibility',
    'Mental Wellness'
  ];

  const handleOverallGoalToggle = (goal: string) => {
    setEditingOverallGoals(prev => 
      prev.includes(goal) 
        ? prev.filter(g => g !== goal) 
        : [...prev, goal]
    );
  };

  const handleSaveOverallGoals = async () => {
    try {
      await updateProfile({ primary_goals: editingOverallGoals });
      setShowOverallGoalsEditor(false);
      toast.success(t('components.goalCard.toast.overallGoalsUpdated'));
    } catch (error) {
      toast.error(t('components.goalCard.toast.updateGoalsFailed'));
    }
  };

  const openOverallGoalsEditor = () => {
    setEditingOverallGoals(profile?.primary_goals || []);
    setShowOverallGoalsEditor(true);
  };

  const handleAnalyzeGoal = (goal: Goal) => {
    // Navigate to chat with structured goal data
    const goalMessage = `Analyze my ${goal.goal_type} goal: ${goal.exercise_name}. Current: ${goal.current_value} ${goal.target_unit}, Target: ${goal.target_value} ${goal.target_unit}. Progress: ${Math.round(getProgressPercentage(goal))}%`;
    
    navigate('/chat', { 
      state: { 
        prefilledMessage: goalMessage,
        goalContext: {
          goal_id: goal.id,
          goal_type: goal.goal_type,
          exercise_name: goal.exercise_name,
          current_value: goal.current_value,
          target_value: goal.target_value,
          target_unit: goal.target_unit,
          progress_percentage: getProgressPercentage(goal)
        }
      } 
    });
  };

  const handleCreateGoal = () => {
    console.log('Attempting to create goal with data:', newGoal);
    
    if (!newGoal.exercise_name && newGoal.goal_type !== 'running') {
      console.error('Validation failed: Missing exercise name');
      toast.error(t('components.goalCard.toast.exerciseNameRequired'));
      return;
    }
    
    if (!newGoal.target_value || newGoal.target_value <= 0) {
      console.error('Validation failed: Invalid target value');
      toast.error(t('components.goalCard.toast.targetValueRequired'));
      return;
    }
    
    if (newGoal.goal_type === 'running' && (!newGoal.description || parseFloat(newGoal.description) <= 0)) {
      console.error('Validation failed: Missing or invalid target time for running goal');
      toast.error(t('components.goalCard.toast.targetTimeRequired'));
      return;
    }
    
    console.log('Validation passed, calling onCreateGoal');
    // Convert empty string to null for target_date
    const goalData = {
      ...newGoal,
      target_date: newGoal.target_date || null
    };
    onCreateGoal(goalData);
    setNewGoal({
      goal_type: 'strength',
      exercise_name: '',
      target_value: 0,
      target_unit: 'kg',
      current_value: 0,
      starting_value: null,
      target_date: null,
      is_active: true,
      description: ''
    });
    setShowCreateDialog(false);
  };

  const handleUpdateProgress = () => {
    if (selectedGoal && newProgress) {
      const value = selectedGoal.goal_type === 'running' 
        ? parseTimeToMinutes(newProgress)
        : parseFloat(newProgress);
      onUpdateProgress(selectedGoal.id, value);
      
      setShowUpdateDialog(false);
      setNewProgress('');
      setSelectedGoal(null);
    }
  };

  const handleDeleteGoal = () => {
    if (selectedGoal) {
      onDeleteGoal(selectedGoal.id);
      setShowUpdateDialog(false);
      setSelectedGoal(null);
      toast.success(t('components.goalCard.toast.goalRemoved'));
    }
  };

  const getProgressPercentage = (goal: Goal) => getGoalProgressPercentage(goal);
  
  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.round((minutes % 1) * 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const parseTimeToMinutes = (timeStr: string): number => {
    const parts = timeStr.split(':');
    if (parts.length === 3) {
      const [hours, mins, secs] = parts.map(p => parseInt(p) || 0);
      return hours * 60 + mins + secs / 60;
    }
    return parseFloat(timeStr) || 0;
  };

  const overallGoals = profile?.primary_goals || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-normal">
          <Target className="h-5 w-5 text-primary" />
          My Goals
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Goals Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('components.goalCard.overallSection')}</h3>
            {!showOverallGoalsEditor ? (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={openOverallGoalsEditor}
                className="h-7 text-xs"
              >
                <Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setShowOverallGoalsEditor(false)}
                  className="h-7 text-xs text-muted-foreground"
                >
                  <X className="h-3 w-3" />
                </Button>
                <Button 
                  size="sm" 
                  variant="default" 
                  onClick={handleSaveOverallGoals}
                  className="h-7 text-xs"
                >
                  <Check className="h-3 w-3 mr-1" />
                  Save
                </Button>
              </div>
            )}
          </div>
          
          {showOverallGoalsEditor ? (
            <div className="grid grid-cols-2 gap-2">
              {primaryGoalOptions.map((goal) => (
                <div
                  key={goal}
                  className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                    editingOverallGoals.includes(goal)
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleOverallGoalToggle(goal)}
                >
                  <Checkbox
                    checked={editingOverallGoals.includes(goal)}
                    onCheckedChange={() => handleOverallGoalToggle(goal)}
                    className="pointer-events-none h-4 w-4"
                  />
                  <span className="text-sm">{goal}</span>
                </div>
              ))}
            </div>
          ) : overallGoals.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {overallGoals.map((goal: string) => (
                <Badge key={goal} variant="secondary" className="text-xs py-1.5 px-3 bg-primary text-black font-semibold">
                  {goal}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('components.goalCard.emptyOverall')} <button onClick={openOverallGoalsEditor} className="text-primary hover:underline">{t('components.goalCard.addOverall')}</button>
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Specific Goals Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('components.goalCard.specificSection')}</h3>
            {canAddGoal ? (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs">
                  <Plus className="h-3 w-3 mr-1" />
                  New Goal
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('components.goalCard.createGoalTitle')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="goalType">{t('components.goalCard.goalTypeLabel')}</Label>
                    <Select value={newGoal.goal_type} onValueChange={(value) => setNewGoal({...newGoal, goal_type: value, target_unit: value === 'running' ? defaultDistanceUnit : value === 'endurance' ? 'reps' : defaultWeightUnit})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="strength">{t('components.goalCard.goalTypeStrength')}</SelectItem>
                        <SelectItem value="endurance">{t('components.goalCard.goalTypeEndurance')}</SelectItem>
                        <SelectItem value="running">{t('components.goalCard.goalTypeRunning')}</SelectItem>
                        <SelectItem value="weight">{t('components.goalCard.goalTypeWeight')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {newGoal.goal_type !== 'running' && (
                    <div>
                      <Label htmlFor="exercise">
                        {newGoal.goal_type === 'strength' ? 'Exercise' : 
                         newGoal.goal_type === 'endurance' ? 'Exercise' : 
                         newGoal.goal_type === 'weight' ? 'Metric' : 'Exercise/Metric'}
                      </Label>
                      <Input
                        id="exercise"
                        value={newGoal.exercise_name}
                        onChange={(e) => setNewGoal({...newGoal, exercise_name: e.target.value})}
                        placeholder={
                          newGoal.goal_type === 'strength' ? 'e.g., Bench Press, Squat, Deadlift' :
                          newGoal.goal_type === 'endurance' ? 'e.g., Pull-ups, Push-ups, Plank' :
                          newGoal.goal_type === 'weight' ? 'e.g., Body Weight, Body Fat %' :
                          'e.g., Exercise name'
                        }
                      />
                    </div>
                  )}

                  {newGoal.goal_type === 'running' ? (
                    <>
                      <div>
                        <Label htmlFor="distance">Race Distance</Label>
                        <Select 
                          value={newGoal.exercise_name} 
                          onValueChange={(value) => setNewGoal({...newGoal, exercise_name: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select distance" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5K">5K</SelectItem>
                            <SelectItem value="10K">10K</SelectItem>
                            <SelectItem value="Half Marathon">Half Marathon</SelectItem>
                            <SelectItem value="Marathon">Marathon</SelectItem>
                            <SelectItem value="Custom Distance">Custom Distance</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="targetDistance">Target Distance (km)</Label>
                          <Input
                            id="targetDistance"
                            type="number"
                            step="0.1"
                            value={newGoal.target_value || ''}
                            onChange={(e) => setNewGoal({...newGoal, target_value: parseFloat(e.target.value) || 0})}
                            placeholder="e.g., 5, 10, 21.1, 42.2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="targetTime">Target Time (minutes)</Label>
                          <Input
                            id="targetTime"
                            type="number"
                            step="0.1"
                            value={newGoal.description || ''}
                            onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                            placeholder="e.g., 20, 45, 90, 180"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="targetValue">{t('components.goalCard.targetLabel')}</Label>
                        <Input
                          id="targetValue"
                          type="number"
                          value={newGoal.target_value || ''}
                          onChange={(e) => setNewGoal({...newGoal, target_value: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="unit">Unit</Label>
                        <Select value={newGoal.target_unit} onValueChange={(value) => setNewGoal({...newGoal, target_unit: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="lbs">lbs</SelectItem>
                            <SelectItem value="reps">reps</SelectItem>
                            <SelectItem value="minutes">minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="currentValue">{t('components.goalCard.currentValueLabel')}</Label>
                    <Input
                      id="currentValue"
                      type="number"
                      value={newGoal.current_value || ''}
                      onChange={(e) => setNewGoal({...newGoal, current_value: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="targetDate">{t('components.goalCard.deadlineLabel')}</Label>
                    <Input
                      id="targetDate"
                      type="date"
                      value={newGoal.target_date || ''}
                      onChange={(e) => setNewGoal({...newGoal, target_date: e.target.value || null})}
                    />
                  </div>
                  <Button onClick={handleCreateGoal} className="w-full">
                    Create Goal
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            ) : (
              <button
                type="button"
                onClick={() => openPaywall({ description: t('premium.maxActiveGoals'), source: 'goals_limit' })}
                className="text-[10px] text-primary max-w-[9rem] text-right leading-tight underline underline-offset-2"
              >
                {t('premium.maxActiveGoals', {
                  defaultValue: '1 goal on Free — upgrade for more',
                })}
              </button>
            )}
          </div>
          
          {goals.length === 0 ? (
            <div className="text-center py-6">
              <Target className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">{t('components.goalCard.emptySpecific')}</p>
              <p className="text-xs text-muted-foreground">
                Create measurable goals like "Bench Press 100kg" to track your progress!
              </p>
            </div>
          ) : (
          <div className="space-y-3">
            {goals.map((goal) => {
              const percentage = getProgressPercentage(goal);

              return (
                <Collapsible key={goal.id} className="rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="p-4">
                    {/* Collapsed Header - Always Visible */}
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200 flex-shrink-0 data-[state=open]:rotate-180" />
                          <div className="flex-1 min-w-0 text-left">
                            <h3 className="text-sm sm:text-base font-medium text-foreground truncate">{goal.exercise_name}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-muted-foreground capitalize">{goal.goal_type} Goal</p>
                              <span className="text-xs text-muted-foreground">•</span>
                              <p className="text-xs text-foreground font-medium">
                                {goal.goal_type === 'running'
                                  ? formatTime(goal.current_value)
                                  : `${goal.current_value} ${goal.target_unit}`
                                }
                                <span className="text-muted-foreground font-normal"> / {goal.goal_type === 'running' 
                                  ? formatTime(parseFloat(goal.description || '0'))
                                  : `${goal.target_value} ${goal.target_unit}`
                                }</span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-lg sm:text-xl font-bold text-primary">{Math.round(percentage)}%</div>
                            <div className="text-xs text-muted-foreground">{t('components.goalCard.progressLabel')}</div>
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    {/* Expanded Content */}
                    <CollapsibleContent className="pt-4">
                      <div className="space-y-4">
                        {/* Update Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedGoal(goal);
                            setNewProgress(goal.current_value.toString());
                            setShowUpdateDialog(true);
                          }}
                          className="border-primary/30 hover:border-primary/50 h-8 w-full"
                        >
                          <Edit3 className="h-3 w-3 mr-2" />
                          Update Progress
                        </Button>

                        {/* Progress Details */}
                        <div className="flex items-center gap-4 sm:gap-8">
                          {/* Progress Circle */}
                          <div className="relative flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" style={{ filter: 'drop-shadow(0 0 8px hsl(var(--primary) / 0.5))' }}>
                              <defs>
                                <linearGradient id={`gradient-${goal.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="hsl(var(--primary))" />
                                  <stop offset="50%" stopColor="hsl(var(--primary) / 0.8)" />
                                  <stop offset="100%" stopColor="hsl(var(--primary) / 0.6)" />
                                </linearGradient>
                              </defs>
                              <circle
                                cx="50"
                                cy="50"
                                r="42"
                                stroke={`url(#gradient-${goal.id})`}
                                strokeWidth="10"
                                fill="transparent"
                                strokeDasharray={`${2 * Math.PI * 42}`}
                                strokeDashoffset={`${2 * Math.PI * 42 * (1 - percentage / 100)}`}
                                className="transition-all duration-1000 ease-out"
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                              <span className="text-2xl sm:text-3xl font-black text-primary">{Math.round(percentage)}%</span>
                              <span className="text-[9px] sm:text-xs text-muted-foreground font-semibold mt-0.5">{t('components.goalCard.progressLabel')}</span>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="flex-1 space-y-2 sm:space-y-3">
                            {/* Target Goal */}
                            <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-2 sm:p-3 rounded-lg border border-primary/20">
                              <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('components.goalCard.targetGoalLabel')}</div>
                              <div className="text-base sm:text-lg font-bold text-foreground">
                                {goal.goal_type === 'running' 
                                  ? formatTime(parseFloat(goal.description || '0'))
                                  : `${goal.target_value} ${goal.target_unit}`
                                }
                              </div>
                            </div>

                            {/* Current Progress */}
                            <div className="bg-muted/30 p-2 sm:p-3 rounded-lg">
                              <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('components.goalCard.currentLabel')}</div>
                              <div className="text-base sm:text-lg font-bold text-foreground">
                                {goal.goal_type === 'running'
                                  ? formatTime(goal.current_value)
                                  : `${goal.current_value} ${goal.target_unit}`
                                }
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Analyze Button */}
                        <Button
                          onClick={() => handleAnalyzeGoal(goal)}
                          className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground shadow-md hover:shadow-lg transition-all"
                          size="sm"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Analyze with {profile?.ai_coach_name || 'Arnold'}
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
          )}
        </div>

        {/* Update Progress Dialog */}
        <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Progress: {selectedGoal?.exercise_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newProgress">
                  {selectedGoal?.goal_type === 'running' ? 'New Time (HH:MM:SS)' : 'New Progress'}
                </Label>
                <Input
                  id="newProgress"
                  type={selectedGoal?.goal_type === 'running' ? 'text' : 'number'}
                  step="0.1"
                  value={newProgress}
                  onChange={(e) => setNewProgress(e.target.value)}
                  placeholder={selectedGoal?.goal_type === 'running' ? '00:25:30' : `Current: ${selectedGoal?.current_value}`}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleUpdateProgress} className="flex-1">
                  Update Progress
                </Button>
                <Button 
                  onClick={handleDeleteGoal} 
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Goal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
